import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VIPBookingSection = ({ fullPage = false }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event: '',
    guests: '4',
    message: '',
  });

  const events = [
    { id: 'openair-2025', name: 'Open Air 2025 - 11 Juillet' },
    { id: 'mirano-april-2025', name: 'Mirano - 4 Avril 2025' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/vip-booking`, {
        ...formData,
        guests: parseInt(formData.guests),
        language: i18n.language,
      });

      if (response.data.success) {
        toast.success(t('booking.success'));
        setWhatsappLink(response.data.whatsapp_link);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          event: '',
          guests: '4',
          message: '',
        });
      }
    } catch (error) {
      toast.error(t('booking.error'));
    } finally {
      setLoading(false);
    }
  };

  const sectionClass = fullPage 
    ? 'py-24 sm:py-32 bg-[#060606]' 
    : 'py-16 sm:py-24 bg-[#0B0B0C]';

  return (
    <section 
      className={sectionClass}
      data-testid="vip-booking-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F0E8] mb-3"
          >
            {t('booking.title')}
          </motion.h2>
          <p className="font-serif italic text-lg text-[#B9B2A8]">
            {t('booking.subtitle')}
          </p>
        </div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
                {t('booking.name')}
              </label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/10 text-[#F5F0E8] focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                data-testid="booking-name-input"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
                {t('booking.email')}
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/10 text-[#F5F0E8] focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                data-testid="booking-email-input"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
                {t('booking.phone')}
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/10 text-[#F5F0E8] focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                data-testid="booking-phone-input"
              />
            </div>

            {/* Event */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
                {t('booking.event')}
              </label>
              <Select 
                value={formData.event} 
                onValueChange={(value) => setFormData((prev) => ({ ...prev, event: value }))}
                required
              >
                <SelectTrigger 
                  className="bg-white/5 border-white/10 text-[#F5F0E8] focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                  data-testid="booking-event-select"
                >
                  <SelectValue placeholder="Sélectionnez un événement" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0B0C] border-white/10">
                  {events.map((event) => (
                    <SelectItem 
                      key={event.id} 
                      value={event.id}
                      className="text-[#F5F0E8] focus:bg-[#C9A84C]/20 focus:text-[#F5F0E8]"
                    >
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Guests */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
                {t('booking.guests')}
              </label>
              <Select 
                value={formData.guests} 
                onValueChange={(value) => setFormData((prev) => ({ ...prev, guests: value }))}
              >
                <SelectTrigger 
                  className="bg-white/5 border-white/10 text-[#F5F0E8] focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                  data-testid="booking-guests-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0B0C] border-white/10">
                  {[2, 4, 6, 8, 10, 12, 15, 20].map((num) => (
                    <SelectItem 
                      key={num} 
                      value={String(num)}
                      className="text-[#F5F0E8] focus:bg-[#C9A84C]/20 focus:text-[#F5F0E8]"
                    >
                      {num} personnes
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
              {t('booking.message')}
            </label>
            <Textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="bg-white/5 border-white/10 text-[#F5F0E8] focus:border-[#C9A84C] focus:ring-[#C9A84C] resize-none"
              data-testid="booking-message-input"
            />
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A] font-display uppercase tracking-wider gold-glow btn-active"
              data-testid="booking-submit-button"
            >
              {loading ? 'Envoi...' : t('booking.submit')}
            </Button>
            <a
              href={whatsappLink || `https://wa.me/32478814497?text=${encodeURIComponent('Bonjour, je souhaite réserver une table VIP.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 font-display uppercase tracking-wider"
                data-testid="booking-whatsapp-button"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {t('booking.whatsapp')}
              </Button>
            </a>
          </div>

          {/* Success WhatsApp Link */}
          {whatsappLink && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg text-center"
            >
              <p className="text-sm text-[#F5F0E8] mb-2">
                Vous pouvez également nous contacter directement sur WhatsApp:
              </p>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#25D366] hover:underline"
                data-testid="booking-whatsapp-success-link"
              >
                <MessageCircle className="w-4 h-4" />
                Ouvrir WhatsApp
              </a>
            </motion.div>
          )}
        </motion.form>
      </div>
    </section>
  );
};

export default VIPBookingSection;
